var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { z } from 'zod';
import { getPusherFromWindow } from 'utils/window';
import { AccountIdSchema } from 'types/accounts';
import { GameIdSchema } from 'types/games';
var pusherSingleton;
var GameUpdateDataSchema = z.object({
    game_id: GameIdSchema
});
var AccountUpdateDataSchema = z.object({
    account_id: AccountIdSchema
});
var ActivateAppDataSchema = z.unknown();
/**
 * The gong moment (SCO-636): one office-wide event per weighted moment,
 * published by the backend on the account channel. Discriminated on `kind`
 * so a trackable gong always carries its trackable and a board clear never
 * does; anything else is dropped at the boundary.
 */
var GongBaseSchema = z.object({
    v: z.literal(1),
    producerId: z.number().int().positive(),
    producerName: z.string().min(1),
    localDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/)
});
export var GongEventSchema = z.discriminatedUnion('kind', [
    GongBaseSchema.extend({ kind: z.literal('board_clear') }).strict(),
    GongBaseSchema.extend({
        kind: z.literal('trackable'),
        trackableId: z.number().int().positive(),
        trackableName: z.string().min(1)
    }).strict()
]);
/**
 * Where the Pusher client connects (SCO-667). The three values are build-time
 * constants from the webpack DefinePlugin; unset or empty means the hosted
 * Pusher service, a ws host points the client at a Pusher-protocol server
 * (soketi inside the unified e2e stack). They come from the build environment,
 * so they are parsed at this boundary.
 */
var OptionalEnvStringSchema = z
    .string()
    .trim()
    .optional()
    .transform(function (value) { return (value === '' ? undefined : value); });
var PortSchema = z
    .string()
    .regex(/^\d+$/, 'expected a base-10 port number')
    .transform(Number)
    .pipe(z.number().int().min(1).max(65535));
var PusherClientOptionsInputSchema = z.object({
    wsHost: OptionalEnvStringSchema,
    wsPort: OptionalEnvStringSchema.pipe(PortSchema.optional()),
    // Literal 'true' | 'false' only, default true. Never Boolean('false').
    tls: OptionalEnvStringSchema
        .pipe(z.enum(['true', 'false']).optional())
        .transform(function (value) { return (value === undefined ? true : value === 'true'); })
});
export var buildPusherClientOptions = function (input) {
    var _a = PusherClientOptionsInputSchema.parse(input), wsHost = _a.wsHost, wsPort = _a.wsPort, tls = _a.tls;
    if (wsHost === undefined) {
        return {
            wsHost: 'ws.pusherapp.com',
            httpHost: 'sockjs.pusher.com',
            encrypted: true
        };
    }
    // No port → leave the keys out entirely: pusher-js 4.1 merges options with
    // Collections.extend, which would copy an explicit undefined over its
    // default port and dial `host:undefined`.
    return __assign(__assign({ wsHost: wsHost }, (wsPort === undefined ? {} : { wsPort: wsPort, wssPort: wsPort })), { encrypted: tls, enabledTransports: ['ws', 'wss'], disableStats: true, httpHost: wsHost });
};
var getPusher = function () {
    var pusherFromWindow = getPusherFromWindow();
    if (!pusherFromWindow) {
        throw new Error('Pusher is not defined');
    }
    return pusherFromWindow;
};
/**
 * Initialize the Pusher instance
 * @param pusherKey - The Pusher key
 */
export var initPusher = function (pusherKey) {
    if (pusherSingleton) {
        return;
    }
    var options = buildPusherClientOptions({
        wsHost: SBB_PUSHER_WS_HOST,
        wsPort: SBB_PUSHER_WS_PORT,
        tls: SBB_PUSHER_WS_TLS
    });
    var PusherClass = getPusher();
    pusherSingleton = new PusherClass(pusherKey, options);
};
export var getAccountChannelName = function (accountId) { return "account-".concat(accountId); };
export var getGameChannelName = function (gameId) { return "game-".concat(gameId); };
export var getActivateAppChannelName = function (activationCode) { return "activate-".concat(activationCode); };
export var unsubscribeFromChannel = function (getChannelName) {
    if (!pusherSingleton) {
        return;
    }
    var channelName = getChannelName();
    pusherSingleton.unsubscribe(channelName);
};
/**
 * Subscribe to the account channel
 * @param accountId - The account ID
 * @param callback - The callback function
 */
export var subscribeToAccountChannel = function (accountId, callback) {
    if (!pusherSingleton) {
        return;
    }
    var channelName = getAccountChannelName(accountId);
    var channel = pusherSingleton.subscribe(channelName);
    // Unbind any existing 'update' listeners to prevent duplicate event handlers
    // This makes the function idempotent and safe to call multiple times (e.g., on HMR reload)
    channel.unbind('update');
    /**
     * Because the Pusher is not properly typed, I'm verifying the data before calling the callback
     * I think there should be a way in TS to better type the "subscribe" method so that the "data"
     * is typed When passed to the callback.
     */
    channel.bind('update', function (rawData) {
        var result = AccountUpdateDataSchema.safeParse(rawData);
        if (result.success) {
            callback(result.data);
        }
        else {
            console.log('rawData', rawData);
            console.error('Invalid account update data:', result.error);
        }
    });
};
/**
 * Bind the gong moment on the account channel (SCO-636). The channel is the
 * one DataUtils already subscribes for `update`; this adds a second event and
 * returns a disposer that unbinds exactly this handler — never the channel,
 * never `update`. Invalid payloads are dropped with one warning.
 */
export var subscribeToAccountGong = function (accountId, callback) {
    if (!pusherSingleton) {
        return function () { return undefined; };
    }
    var channel = pusherSingleton.subscribe(getAccountChannelName(accountId));
    var handler = function (rawData) {
        var _a;
        var result = GongEventSchema.safeParse(rawData);
        if (result.success) {
            callback(result.data);
        }
        else {
            console.warn('Invalid gong event dropped:', (_a = result.error.issues[0]) === null || _a === void 0 ? void 0 : _a.message);
        }
    };
    channel.bind('gong', handler);
    return function () { return channel.unbind('gong', handler); };
};
/**
 * Subscribe to the game channel
 * @param gameId - The game ID
 * @param callback - The callback function
 */
export var subscribeToGameChannel = function (gameId, callback) {
    if (!pusherSingleton) {
        return;
    }
    var channelName = getGameChannelName(gameId);
    var channel = pusherSingleton.subscribe(channelName);
    // Unbind any existing 'update' listeners to prevent duplicate event handlers
    // This makes the function idempotent and safe to call multiple times (e.g., on HMR reload)
    channel.unbind('update');
    /**
     * Because the Pusher is not properly typed, I'm verifying the data before calling the callback
     * I think there should be a way in TS to better type the "subscribe" method so that the "data"
     * is typed When passed to the callback.
     */
    channel.bind('update', function (rawData) {
        var result = GameUpdateDataSchema.safeParse(rawData);
        if (result.success) {
            callback(result.data);
        }
        else {
            console.log('rawData', rawData);
            console.error('Invalid game update data:', result.error);
        }
    });
};
/**
 * Subscribe to the activate app channel
 * @param activationCode - The activation code
 * @param callback - The callback function
 */
export var subscribeToActivateAppChannel = function (activationCode, callback) {
    if (!pusherSingleton) {
        return;
    }
    var channelName = getActivateAppChannelName(activationCode);
    var channel = pusherSingleton.subscribe(channelName);
    channel.bind('activate', callback);
};
